'use client';

import { useState, useEffect, Fragment, useRef, useCallback } from 'react';
import { EmblaCarouselType, EmblaOptionsType, EmblaPluginType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay, { AutoplayType } from 'embla-carousel-autoplay';
import Image, { StaticImageData } from 'next/image';
import { isStaticImageData } from '@/utility/type-utility-methods';
import { ProjectDataType } from '@/@types/projects';
import styles from './LoginPageCarousel.module.scss';

const options: EmblaOptionsType = {
  loop: true,
};

// create own timer
// const plugins: EmblaPluginType[] = [Autoplay({
//     delay: delayInMilliseconds,
//     stopOnInteraction: true,
//     stopOnMouseEnter: true,
//     stopOnFocusIn: true,
// })];

export default function LoginPageCarousel({
  images,
}: {
  images: (string | StaticImageData)[];

})
{
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [progress, setProgress] = useState<number>(50);

  // progress timer refs
  let timerIntervalRef = useRef<number>(4000); //in miliseconds
  let currentTimeRef = useRef<number>(0);
  let isPausedRef = useRef<boolean>(false);
  let fpsRef = useRef<number>(60);

  const HandleInit = useCallback((emblaApi: EmblaCarouselType) =>
  {
    emblaApi && setScrollSnaps(emblaApi!.scrollSnapList());
  }, []);

  const HandleSelect = useCallback((emblaApi: EmblaCarouselType) =>
  {
    setSelectedIndex(emblaApi.selectedScrollSnap());

    // if our selection changes change progress to 0 and reset the timer
    setProgress(0);
    currentTimeRef.current = 0;
  }, [emblaApi]);

  const HandleScrollTo = useCallback((index: number) =>
  {
    emblaApi?.scrollTo(index);
  }, [emblaApi]);

  const HandleProgress = useCallback(() =>
  {
    // check if paused
    if (isPausedRef.current)
    {
      setTimeout(() =>
      {
        requestAnimationFrame(HandleProgress);
      }, 1000 / fpsRef.current);

      return;
    }

    currentTimeRef.current += 1000 / fpsRef.current;

    let progress = +(currentTimeRef.current / timerIntervalRef.current * 100).toFixed(0);

    // check if progress exceeds 100
    if (+progress >= 100)
    {
      emblaApi?.scrollNext();
      currentTimeRef.current = 0;
      progress = 0;
    }

    setProgress(+progress);

    setTimeout(() =>
    {
      requestAnimationFrame(HandleProgress);
    }, 1000 / fpsRef.current);

  }, [emblaApi]);


  useEffect(() =>
  {
    if (!emblaApi) return;

    HandleInit(emblaApi);
    HandleSelect(emblaApi);

    emblaApi.on('reInit', HandleInit);
    emblaApi.on('reInit', HandleSelect);
    emblaApi.on('select', HandleSelect);

    HandleProgress();

  }, [emblaApi, HandleInit, HandleSelect]);

  return (
    <Fragment>
      <div className={ `embla ${ styles.emblaLogin }` }
        onMouseEnter={ () => isPausedRef.current = true }
        onMouseLeave={ () =>
        {
          // changed from state to ref 
          isPausedRef.current = false;

          // when the user is interacting with the embla instance then leaves we should play the autoplay
          // const autoplay = emblaApi?.plugins().autoplay as AutoplayType;
          // autoplay.play();
        } }>
        <div className='embla__viewport' ref={ emblaRef }>

          <div className="embla__container">
            { images.map((image, index) =>
            {
              return (
                <div
                  className="embla__slide"
                  key={ `$ slide ${ index }` }>
                  <Image
                    priority
                    src={ isStaticImageData(image) ? image.src : image }
                    key={ index }
                    width="1920"
                    height="1080"
                    alt={ `slide image ${ index }` } />
                </div>
              );
            }) }
          </div>
        </div>

        <div
          className={ styles.dots }
          style={ { '--progress': `${ progress.toString() }%` } as React.CSSProperties }>
          { scrollSnaps.map((__, index) =>
          {
            return (
              <div
                className={ [
                  styles.dot,
                  index < selectedIndex ? styles.viewed : '',
                  index === selectedIndex ? styles.selected : '',
                ].filter(Boolean).join(' ') }
                key={ index }
                onClick={ () => HandleScrollTo(index) }>
              </div>
            );
          }) }
        </div>
      </div>
    </Fragment>
  );
};
